/**
 * RenderScaler — Canvas DPR (Device Pixel Ratio) Scaling & Frame Skip
 *
 * Adapted from AstroMonix LOD: render-level scaling is SECONDARY/TERTIARY.
 * Only activates DPR reduction at extreme quality tier.
 * Frame skipping only at extreme tier.
 *
 * Key insight: polygon/sprite reduction (via visualEvolution) should handle
 * performance first before touching canvas resolution.
 */

export interface RenderScalerState {
  /** Current effective DPR */
  currentDPR: number;
  /** Whether DPR was reduced from device native */
  isReduced: boolean;
  /** Current frame-skip divisor */
  frameSkip: number;
  /** Whether this frame should be rendered */
  shouldRender: boolean;
  /** Whether to render-on-demand (skip if scene static + busy) */
  renderOnDemand: boolean;
}

export class RenderScaler {
  private _deviceDPR: number;
  private _currentDPR: number;
  private _frameSkipCounter: number = 0;
  private _lastTargetDPR: number = -1;

  constructor() {
    this._deviceDPR = Math.min(window.devicePixelRatio || 1, 2); // Cap at 2x
    this._currentDPR = this._deviceDPR;
  }

  /**
   * Update scaler state each frame.
   * @param targetDPR - Desired DPR from quality tier (-1 = use device DPR)
   * @param frameSkipRate - Frame skip divisor from quality tier (1 = every frame)
   * @param renderOnDemand - Whether to skip frames when scene is static
   * @param sceneDirty - Whether the scene has changed
   * @returns Current render scaler state
   */
  update(
    targetDPR: number,
    frameSkipRate: number,
    renderOnDemand: boolean,
    sceneDirty: boolean
  ): RenderScalerState {
    // Determine effective DPR
    const effectiveDPR = targetDPR < 0 ? this._deviceDPR : targetDPR;
    const dprChanged = Math.abs(effectiveDPR - this._currentDPR) > 0.02;

    if (dprChanged) {
      this._currentDPR = effectiveDPR;
      this._lastTargetDPR = targetDPR;
    }

    // Frame skip logic
    this._frameSkipCounter++;
    const shouldSkipByRate = frameSkipRate > 1 &&
      (this._frameSkipCounter % frameSkipRate) !== 0;

    // Render-on-demand: skip if scene hasn't changed and system wants on-demand
    const skipOnDemand = renderOnDemand && !sceneDirty;

    return {
      currentDPR: this._currentDPR,
      isReduced: this._currentDPR < this._deviceDPR,
      frameSkip: frameSkipRate,
      shouldRender: !shouldSkipByRate && !skipOnDemand,
      renderOnDemand
    };
  }

  /**
   * Apply DPR scaling to a canvas element.
   * Call when DPR has changed to resize the canvas backing store.
   */
  applyToCanvas(canvas: HTMLCanvasElement, displayWidth: number, displayHeight: number): boolean {
    const targetWidth = Math.round(displayWidth * this._currentDPR);
    const targetHeight = Math.round(displayHeight * this._currentDPR);

    if (canvas.width !== targetWidth || canvas.height !== targetHeight) {
      canvas.width = targetWidth;
      canvas.height = targetHeight;
      return true;
    }
    return false;
  }

  /** Get device (native) DPR */
  get deviceDPR(): number { return this._deviceDPR; }

  /** Get current effective DPR */
  get currentDPR(): number { return this._currentDPR; }

  /** Reset to device DPR */
  reset(): void {
    this._currentDPR = this._deviceDPR;
    this._frameSkipCounter = 0;
    this._lastTargetDPR = -1;
  }

  /** Refresh device DPR (call on window resize or DPR change) */
  refreshDeviceDPR(): void {
    this._deviceDPR = Math.min(window.devicePixelRatio || 1, 2);
    if (this._lastTargetDPR < 0) {
      this._currentDPR = this._deviceDPR;
    }
  }
}

// Singleton
let _rsInst: RenderScaler | null = null;
export function getRenderScaler(): RenderScaler {
  if (!_rsInst) _rsInst = new RenderScaler();
  return _rsInst;
}
